import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuorums } from '../hooks/useQuorums';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { formatTimeAgo, formatNumber } from '../util/format';
import type { QuorumDetail, QuorumRow, QuorumSetRow } from '../api/types';

export const QuorumsPage = () => {
  const { data, loading, error, refetch, pollInterval, pollTick } = useQuorums();
  const [selectedQuorum, setSelectedQuorum] = useState<QuorumDetail | null>(null);

  if (loading && !data) return <LoadingSpinner message="Loading quorum status..." />;
  if (error) return <ErrorMessage error={error} retry={refetch} />;
  if (!data || !data.summary) return <div>No quorum data available</div>;

  const { summary } = data;

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'perfect':
      case 'healthy':
      case 'good':
      case 'excellent':
        return 'status-healthy';
      case 'acceptable':
      case 'stable':
        return 'status-slow';
      case 'attention_needed':
      case 'unhealthy':
      case 'stalled':
        return 'status-stalled';
      default:
        return 'status-unknown';
    }
  };

  const dkgStatusLabel = `DKG: ${summary.dkg_health}`;
  const rotationStatusLabel = `Rotation: ${summary.rotation_health}`;
  const coverageStatusLabel = `Coverage: ${summary.coverage}`;

  return (
    <div className="quorums-page">
      <div className="chainlock-loader">
        <div
          key={pollTick}
          className="chainlock-loader-bar"
          style={{
            animationDuration: `${pollInterval}ms`,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Quorum Monitoring</h1>
          <p className="subtitle" style={{ marginTop: '4px' }}>
            Health and rotation of LLMQ signing quorums, as observed by the quorum monitor
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Link to="/chainlocks" className="nav-button">
            View ChainLocks →
          </Link>
        </div>
      </div>

      {/* High-level Quorum & Finality Metrics */}
      <section className="metrics-row">
        <div className="metric-card">
          <h3>Frontier</h3>
          <div className="metric-value">
            {data.chainlock_height != null ? formatNumber(data.chainlock_height) : 'N/A'}
          </div>
          <div className="metric-label">
            Chain tip: {data.tip_height != null ? formatNumber(data.tip_height) : 'N/A'}
          </div>
        </div>

        <div className="metric-card">
          <h3>Active Quorums</h3>
          <div className="metric-value">{summary.active_quorum_count}</div>
          <div className="metric-label">Currently signing ChainLocks</div>
        </div>

        <div className="metric-card">
          <h3>Overall Status</h3>
          <div className={`metric-value ${getStatusClass(summary.overall_status)}`}>
            {summary.overall_status}
          </div>
          <div className="metric-label">
            {dkgStatusLabel} • {rotationStatusLabel} • {coverageStatusLabel}
          </div>
        </div>
      </section>

      {/* Active Quorums */}
      <h2>Active Quorums</h2>
      <p className="subtitle" style={{ margin: '0 0 16px 0' }}>4 required for healthy operation</p>
      <section className="chainlocks-history">
        <div className="chainlocks-grid active-quorums-grid">
          {(() => {
            // Always show 4 slots (Syscoin requires 4 quorums for healthy operation)
            const REQUIRED_QUORUMS = 4;
            const activeQuorums = [...data.active_quorums];

            // Fill remaining slots with pending placeholders
            while (activeQuorums.length < REQUIRED_QUORUMS) {
              activeQuorums.push(null as any);
            }

            return activeQuorums.slice(0, REQUIRED_QUORUMS).map((q, index) => {
              if (!q) {
                // Pending placeholder
                return (
                  <div
                    key={`pending-${index}`}
                    className="chainlock-box"
                    style={{ opacity: 0.6, cursor: 'default' }}
                  >
                    <div className="chainlock-box-height">Quorum {index + 1}</div>
                    <div className="chainlock-box-status">
                      <span className="badge badge-pending">PENDING</span>
                    </div>
                    <div className="chainlock-box-time">Awaiting DKG</div>
                  </div>
                );
              }

              const memberCount =
                q.memberCount ??
                (q.member_count != null ? q.member_count : null);
              const validMemberCount =
                q.validMemberCount ??
                (q.valid_member_count != null ? q.valid_member_count : null);

              const validPct =
                memberCount && validMemberCount != null && memberCount > 0
                  ? ((validMemberCount / memberCount) * 100).toFixed(1)
                  : null;

              let healthBadge = 'Unknown';
              let healthBadgeClass = 'badge-unknown';
              if (validPct != null) {
                const v = parseFloat(validPct);
                if (v >= 99.5) {
                  healthBadge = 'Excellent';
                  healthBadgeClass = 'badge-excellent';
                } else if (v >= 98) {
                  healthBadge = 'Healthy';
                  healthBadgeClass = 'badge-healthy';
                } else if (v >= 95) {
                  healthBadge = 'Acceptable';
                  healthBadgeClass = 'badge-acceptable';
                } else {
                  healthBadge = 'Degraded';
                  healthBadgeClass = 'badge-degraded';
                }
              }

              return (
                <button
                  key={q.hash}
                  className="chainlock-box active"
                  onClick={() => setSelectedQuorum(q)}
                >
                  <div className="chainlock-box-height">Quorum {index + 1}</div>
                  <div className="chainlock-box-status">
                    <span className={`badge ${healthBadgeClass}`}>{healthBadge}</span>
                  </div>
                  <div className="chainlock-box-time">
                    {validPct != null
                      ? `${validMemberCount}/${memberCount} (${validPct}%)`
                      : 'N/A'}
                  </div>
                  <div className="quorum-badges">
                    <span className="badge badge-muted">#{formatNumber(q.height)}</span>
                  </div>
                </button>
              );
            });
          })()}
        </div>
      </section>

      {/* Quorum Rotation History */}
      <h2>Quorum Set Rotation</h2>
      <section className="chainlocks-history">
        {data.quorum_sets.length > 0 ? (
          <div className="chainlocks-grid">
            {data.quorum_sets.map((set: QuorumSetRow) => {
              const isCurrent = !!set.is_current;
              const lastSeenSec = Math.floor(set.last_seen / 1000);

              return (
                <div
                  key={set.id}
                  className={`chainlock-box ${isCurrent ? 'active' : ''}`}
                >
                  <div className="chainlock-box-height">Set #{set.id}</div>
                  <div className="chainlock-box-status">
                    {isCurrent ? (
                      <span className="badge badge-active">Current</span>
                    ) : (
                      <span className="badge badge-muted">
                        {set.rotation_type || 'normal'}
                      </span>
                    )}
                  </div>
                  <div className="chainlock-box-time">
                    {formatTimeAgo(lastSeenSec)}
                  </div>
                  <div className="quorum-badges">
                    {isCurrent && (
                      <span className="badge badge-muted">
                        {set.rotation_type || 'normal'}
                      </span>
                    )}
                    {(set.quorums_added != null || set.quorums_removed != null) && (
                      <span className="badge badge-muted">
                        +{set.quorums_added || 0} / -{set.quorums_removed || 0}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">
            No quorum set history available yet.
          </div>
        )}
      </section>

      {/* DKG & Quorum Details */}
      {data.quorum_info && (
        <>
          <h2>Distributed Key Generation (DKG)</h2>
          {data.quorum_info.last_updated && (
            <p className="subtitle" style={{ margin: '0 0 16px 0' }}>
              Updated {formatTimeAgo(Math.floor(data.quorum_info.last_updated / 1000))}
            </p>
          )}
          <section className="dkg-metrics-row">
            <div className="metric-card">
              <h3>Time Since Last DKG</h3>
              <div className="metric-value">{data.quorum_info.dkg_time_str}</div>
              <div className="metric-label">Session timing</div>
            </div>

            <div className="metric-card">
              <h3>Active DKG Sessions</h3>
              <div className="metric-value">
                {data.quorum_info.dkg_session_count > 0
                  ? data.quorum_info.dkg_session_count
                  : 'None'}
              </div>
              <div className="metric-label">In progress</div>
            </div>

            <div className="metric-card">
              <h3>Minable Commitments</h3>
              <div className="metric-value">
                {data.quorum_info.dkg_minable_commitments > 0
                  ? data.quorum_info.dkg_minable_commitments
                  : 'None'}
              </div>
              <div className="metric-label">Ready to mine</div>
            </div>

            <div className="metric-card">
              <h3>Active Quorums</h3>
              <div className="metric-value">{data.quorum_info.quorum_count}</div>
              <div className="metric-label">LLMQ(s)</div>
            </div>
          </section>
        </>
      )}

      {/* Raw DKG Status Output */}
      {data.quorum_info?.raw_dkg_status ? (
        <>
          <h2>Raw DKG Status</h2>
          <section className="raw-rpc-section">
            <textarea
              className="raw-rpc-output"
              readOnly
              value={JSON.stringify(data.quorum_info.raw_dkg_status as Record<string, any>, null, 2)}
              rows={12}
            />
          </section>
        </>
      ) : null}

      {/* Recent Quorum Log */}
      {(() => {
        // Filter out active quorums from recent list to avoid duplication
        const activeQuorumHashes = new Set(data.active_quorums.map(q => q.hash));
        const historicalQuorums = data.recent_quorums.filter(
          q => !activeQuorumHashes.has(q.quorum_hash)
        );

        return (
          <>
            <h2>Recent Quorums ({historicalQuorums.length})</h2>
            <section className="chainlocks-history">
              {historicalQuorums.length > 0 ? (
                <div className="chainlocks-grid">
                  {historicalQuorums.map((q: QuorumRow) => {
                    const validPct = q.valid_member_count != null && q.member_count
                      ? ((q.valid_member_count / q.member_count) * 100).toFixed(1)
                      : null;

                    // Determine health badge based on valid percentage
                    let healthBadge = 'Unknown';
                    let healthBadgeClass = 'badge-unknown';
                    if (validPct != null) {
                      const v = parseFloat(validPct);
                      if (v >= 99.5) {
                        healthBadge = 'Excellent';
                        healthBadgeClass = 'badge-excellent';
                      } else if (v >= 98) {
                        healthBadge = 'Healthy';
                        healthBadgeClass = 'badge-healthy';
                      } else if (v >= 95) {
                        healthBadge = 'Acceptable';
                        healthBadgeClass = 'badge-acceptable';
                      } else {
                        healthBadge = 'Degraded';
                        healthBadgeClass = 'badge-degraded';
                      }
                    }

                    const handleClick = () => {
                      const detail: QuorumDetail = {
                        hash: q.quorum_hash,
                        height: q.height,
                        minedBlock: q.mined_block || undefined,
                        mined_block: q.mined_block,
                        memberCount: q.member_count ?? null,
                        validMemberCount: q.valid_member_count ?? null,
                        member_count: q.member_count,
                        valid_member_count: q.valid_member_count,
                      };
                      setSelectedQuorum(detail);
                    };

                    return (
                      <button
                        key={q.quorum_hash}
                        className="chainlock-box"
                        onClick={handleClick}
                      >
                        <div className="chainlock-box-height">
                          #{formatNumber(q.height)}
                        </div>
                        <div className="chainlock-box-status">
                          <span className={`badge ${healthBadgeClass}`}>{healthBadge}</span>
                        </div>
                        <div className="chainlock-box-time">
                          {formatTimeAgo(Math.floor(q.last_seen / 1000))}
                        </div>
                        <div className="quorum-badges">
                          {validPct != null && (
                            <span className="badge badge-muted">
                              {q.valid_member_count}/{q.member_count} ({validPct}%)
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
          </div>
        ) : (
          <div className="empty-state">
            No quorum history in database. Run the quorum monitor to populate this view.
          </div>
        )}
            </section>
          </>
        );
      })()}

      {/* Raw Monitor Insights */}
      {data.insights && (
        <>
          <h2>Monitor Insights</h2>
          <section className="raw-rpc-section">
            <textarea
              className="raw-rpc-output"
              readOnly
              value={JSON.stringify(data.insights, null, 2)}
              rows={8}
            />
          </section>
        </>
      )}

      {/* Documentation */}
      <h2>About LLMQ Quorums</h2>
      <div className="navigation-card">
        <p style={{ margin: '0 0 12px 0', lineHeight: 1.6 }}>
          <strong>Long Living Masternode Quorums (LLMQs)</strong> are groups of sentrynodes
          that form signing committees for Syscoin's instant finality features. Each quorum
          uses BLS threshold signatures to collectively sign ChainLocks.
        </p>
        <p style={{ margin: '0 0 12px 0', lineHeight: 1.6 }}>
          Quorums are established through <strong>Distributed Key Generation (DKG)</strong>, a
          cryptographic ceremony where sentrynodes collaboratively create shared signing keys
          without any single node having full control.
        </p>
        <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', lineHeight: 1.8 }}>
          <li>
            <strong>Active Quorums:</strong> Currently signing ChainLocks (typically 4 quorums of ~400 sentrynodes each)
          </li>
          <li>
            <strong>Quorum Rotation:</strong> Periodic replacement of old quorums with newly generated ones
          </li>
          <li>
            <strong>DKG Status:</strong> Shows when the last key generation occurred and if any are in progress
          </li>
          <li>
            <strong>Health Thresholds:</strong> Excellent ≥99.5%, Healthy ≥98%, Acceptable ≥95%, Degraded &lt;95%
          </li>
        </ul>
        <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-text-secondary)' }}>
          A ChainLock requires signatures from at least 60% of active quorums (3 out of 4).
          Each quorum must have a sufficient number of valid members participating to maintain
          network security.
        </p>
      </div>

      {/* Quorum Details Overlay */}
      {selectedQuorum && (
        <div className="chainlock-overlay" onClick={() => setSelectedQuorum(null)}>
          <div className="chainlock-overlay-content" onClick={(e) => e.stopPropagation()}>
            <div className="chainlock-overlay-header">
              <h2>Quorum Details</h2>
              <button
                className="chainlock-overlay-close"
                onClick={() => setSelectedQuorum(null)}
              >
                ✕
              </button>
            </div>
            <div className="chainlock-overlay-body">
              {(() => {
                const q = selectedQuorum;
                const memberCount =
                  q.memberCount ??
                  (q.member_count != null ? q.member_count : null);
                const validMemberCount =
                  q.validMemberCount ??
                  (q.valid_member_count != null ? q.valid_member_count : null);

                const validPct =
                  memberCount && validMemberCount != null && memberCount > 0
                    ? ((validMemberCount / memberCount) * 100).toFixed(1)
                    : null;

                let healthBadge = 'Unknown';
                let healthBadgeClass = 'badge-unknown';
                if (validPct != null) {
                  const v = parseFloat(validPct);
                  if (v >= 99.5) {
                    healthBadge = 'Excellent';
                    healthBadgeClass = 'badge-excellent';
                  } else if (v >= 98) {
                    healthBadge = 'Healthy';
                    healthBadgeClass = 'badge-healthy';
                  } else if (v >= 95) {
                    healthBadge = 'Acceptable';
                    healthBadgeClass = 'badge-acceptable';
                  } else {
                    healthBadge = 'Degraded';
                    healthBadgeClass = 'badge-degraded';
                  }
                }

                return (
                  <>
                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Health Status:</div>
                      <div className="chainlock-detail-value">
                        <span className={`badge ${healthBadgeClass}`}>
                          {healthBadge}
                        </span>
                      </div>
                    </div>

                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Quorum Hash:</div>
                      <div className="chainlock-detail-value hash">{q.hash}</div>
                    </div>

                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Height:</div>
                      <div className="chainlock-detail-value">{formatNumber(q.height)}</div>
                    </div>

                    <div className="chainlock-detail-row">
                      <div className="chainlock-detail-label">Total Members:</div>
                      <div className="chainlock-detail-value">
                        {memberCount != null ? formatNumber(memberCount) : 'N/A'}
                      </div>
                    </div>

                    {validMemberCount != null && (
                      <div className="chainlock-detail-row">
                        <div className="chainlock-detail-label">Valid Members:</div>
                        <div className="chainlock-detail-value">
                          {formatNumber(validMemberCount)} / {formatNumber(memberCount || 0)}
                          {validPct && ` (${validPct}%)`}
                        </div>
                      </div>
                    )}

                    {(q.minedBlock || q.mined_block) && (
                      <div className="chainlock-detail-row">
                        <div className="chainlock-detail-label">Mined in Block:</div>
                        <div className="chainlock-detail-value hash">
                          {String(q.minedBlock || q.mined_block)}
                        </div>
                      </div>
                    )}

                    <div className="chainlock-detail-row" style={{ gridColumn: '1 / -1' }}>
                      <div className="chainlock-detail-label">Health Interpretation:</div>
                      <div className="chainlock-detail-value">
                        <ul style={{ margin: '8px 0 0 20px', color: 'var(--color-text-secondary)', fontSize: '0.85em', lineHeight: '1.6' }}>
                          <li><strong>Excellent (≥99.5%):</strong> Optimal quorum health, all sentrynodes participating</li>
                          <li><strong>Healthy (≥98%):</strong> Good health, minor member fluctuations acceptable</li>
                          <li><strong>Acceptable (≥95%):</strong> Adequate for operation, monitor for degradation</li>
                          <li><strong>Degraded (&lt;95%):</strong> Below threshold, may impact ChainLock reliability</li>
                        </ul>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
